/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { DetailsRow } from 'office-ui-fabric-react/lib/DetailsList';
import { Text } from 'office-ui-fabric-react';
import { isEmpty } from 'lodash';
import DetailsList from '../DetailsList';
import { formatDateString, formatNumberWithCommas } from '../../utils';
import styles from './ComparisonTable.style';

const columns = [
  {
    key: 'statement',
    name: 'Statement',
    fieldName: 'statement',
    styles: { root: { opacity: 0 } },
    columnActionsMode: 0,
    minWidth: 250,
    maxWidth: 250,
    className: styles.firstCell,
    onRender: statement => (
      <>
        <div className={styles.textDate}>
          {formatDateString(statement.statementStartDate, 'MM/dd/yy')} {' - '}
          {formatDateString(statement.statementEndDate, 'MM/dd/yy')}
        </div>
        <Text variant="medium" className={styles.textStatement}>
          {statement.title}
        </Text>
      </>
    ),
  },
  {
    key: 'unitsSold',
    name: 'Units',
    fieldName: 'unitsSold',
    minWidth: 165,
    maxWidth: 165,
    headerClassName: styles.headerColumn,
    className: styles.otherCells,
    columnActionsMode: 0,
    onRender: statement => (
      <span className={styles.rowData}>
        {formatNumberWithCommas(statement?.totalQuantity?.toFixed(2))}
      </span>
    ),
  },
  {
    key: 'royaltiesEarned',
    name: 'Royalties Earned',
    fieldName: 'royaltiesEarned',
    minWidth: 165,
    maxWidth: 165,
    headerClassName: styles.headerColumn,
    className: styles.otherCells,
    columnActionsMode: 0,
    onRender: statement => (
      <span className={styles.rowData}>
        {formatNumberWithCommas(statement.totalRoyaltyEarnedCC?.toFixed(2))}
      </span>
    ),
  },
  {
    key: 'payment',
    name: 'Payment',
    fieldName: 'payment',
    minWidth: 165,
    maxWidth: 165,
    headerClassName: styles.headerColumn,
    className: styles.otherCells,
    columnActionsMode: 0,
    onRender: statement => (
      <span className={styles.rowData}>
        {formatNumberWithCommas(
          statement.totalPayment?.toFixed(2) || statement.totalPaymentCC?.toFixed(2),
        )}
      </span>
    ),
  },
];

const onRenderRow = row => (
  <DetailsRow {...row} className={clsx([row.itemIndex % 2 && styles.oddRow, styles.rowData])} />
);

const ComparisonTable = ({ items, className, ...other }) => {
  let previousContractToDate = [];
  const detailsListStyle = clsx(styles.root, className);
  const currentStatement = {
    ...items.totalRoyalty,
    statementStartDate: items.statementStartDate,
    title: items.statementCurrencyNotEqual
      ? `This Statement (${items.totalRoyalty.statementCC})`
      : `This Statement`,
  };

  if (items.previousStatement) {
    items.previousStatement.title = items.statementCurrencyNotEqual
      ? `Previous Statement (${items.previousStatement.statementCC})`
      : `Previous Statement`;
  }

  if (items.previousContractToDate && !isEmpty(items.previousContractToDate)) {
    previousContractToDate = items.previousContractToDate.map(element => ({
      title: `Previous Contract-to-Date (${element.contractCurrency})`,
      statementStartDate: element.statementStartDate,
      statementEndDate: element.statementEndDate,
      ...element.contractToDate,
    }));
  }

  items.contractToDate.title = items.statementCurrencyNotEqual
    ? `Contract-to-Date (${items.totalRoyalty.statementCC})`
    : 'Contract-to-Date';

  const itemsArray =
    !isEmpty(items.previousStatement) && !isEmpty(items.previousContractToDate)
      ? [currentStatement, items.previousStatement, items.contractToDate, ...previousContractToDate]
      : !isEmpty(items.previousStatement) && isEmpty(items.previousContractToDate)
      ? [currentStatement, items.previousStatement, items.contractToDate]
      : [currentStatement, items.contractToDate];

  const statementColumns =
    items.config?.comparisonDataProjection?.length > 0
      ? columns.filter(x => items.config.comparisonDataProjection.includes(x.key))
      : columns;

  const statementItems = itemsArray.map(item => ({
    ...item,
    currency: items.statementCC,
    statementEndDate: item.statementEndDate || items.statementEndDate,
  }));

  return (
    <>
      {statementItems && (
        <DetailsList
          items={statementItems}
          columns={statementColumns}
          className={detailsListStyle}
          onRenderRow={onRenderRow}
          {...other}
        />
      )}
    </>
  );
};

ComparisonTable.propTypes = {
  items: PropTypes.shape({
    config: PropTypes.object,
    payment: PropTypes.object,
    previousStatement: PropTypes.object,
    contractToDate: PropTypes.object,
    totalRoyalty: PropTypes.object,
    contract: PropTypes.object,
    statementStartDate: PropTypes.string,
    statementEndDate: PropTypes.string,
    previousContractToDate: PropTypes.object,
    statementCurrencyNotEqual: PropTypes.bool,
    statementCC: PropTypes.string,
  }),
  className: PropTypes.string,
};

ComparisonTable.defaultProps = {
  items: [],
  className: '',
};

export default ComparisonTable;
